import { useEffect, useRef, useState } from "react";
import { useLocation, Link } from "react-router-dom";
import { api, formatErr } from "@/lib/api";
import { CheckCircle2, Loader2, XCircle } from "lucide-react";

export default function BookingSuccess() {
  const loc = useLocation();
  const [tx, setTx] = useState(null);
  const [err, setErr] = useState("");
  const polled = useRef(0);
  const params = new URLSearchParams(loc.search);
  const guestToken = params.get("t") || "";

  useEffect(() => {
    const sid = params.get("session_id");
    if (!sid) { setErr("Missing session_id"); return; }
    let alive = true;
    const tick = async () => {
      if (!alive) return;
      polled.current += 1;
      try {
        const url = guestToken ? `/payments/status/${sid}?t=${guestToken}` : `/payments/status/${sid}`;
        const { data } = await api.get(url);
        setTx(data);
        if (data.payment_status === "paid" || polled.current >= 8) return;
      } catch (e) { setErr(formatErr(e)); return; }
      setTimeout(tick, 2000);
    };
    tick();
    return () => { alive = false; };
  }, [loc.search, guestToken]);

  return (
    <div className="max-w-2xl mx-auto px-6 py-20 text-center" data-testid="booking-success-page">
      {!tx && !err && (
        <>
          <Loader2 className="mx-auto animate-spin text-[#0A0A0A]" size={32}/>
          <h1 className="h-display text-3xl mt-6">Confirming your booking…</h1>
        </>
      )}
      {tx?.payment_status === "paid" && (
        <>
          <CheckCircle2 className="mx-auto text-[#0A0A0A]" size={48}/>
          <h1 className="h-display text-4xl mt-6">You&apos;re booked.</h1>
          <p className="text-[#4A4A4A] mt-3">A confirmation is in your bookings.</p>
          <p className="text-sm text-[#8A8A8A] mt-2">${tx.amount?.toFixed(2)} {tx.currency?.toUpperCase()} · paid</p>
          <div className="mt-8 flex justify-center gap-3 flex-wrap">
            {guestToken ? (
              <Link to={`/booking/access/${guestToken}`} className="btn-primary" data-testid="booking-guest-view">
                View my booking
              </Link>
            ) : (
              <Link to="/bookings" className="btn-primary" data-testid="booking-success-view">View bookings</Link>
            )}
            <Link to="/feed" className="btn-secondary">Back to feed</Link>
          </div>
          {guestToken && (
            <p className="text-xs text-[#8A8A8A] mt-4">
              Save this link — bookmark it or check your email. That&apos;s how you&apos;ll re-open your booking + join your call.
            </p>
          )}
        </>
      )}
      {tx && tx.payment_status !== "paid" && (
        <>
          <Loader2 className="mx-auto animate-spin text-[#0A0A0A]" size={32}/>
          <h1 className="h-display text-3xl mt-6">Payment is being processed…</h1>
          <p className="text-[#4A4A4A] mt-3 text-sm">Status: {tx.payment_status}</p>
          <Link to="/bookings" className="btn-secondary mt-8 inline-flex">Check later in Bookings</Link>
        </>
      )}
      {err && (
        <>
          <XCircle className="mx-auto text-[#B84B4B]" size={40}/>
          <h1 className="h-display text-3xl mt-6">Couldn&apos;t confirm</h1>
          <p className="text-[#4A4A4A] mt-3 text-sm">{err}</p>
          <Link to="/bookings" className="btn-secondary mt-8 inline-flex">Open bookings</Link>
        </>
      )}
    </div>
  );
}
